import { normalizeLocale, defaultLocale } from '@/config/locales'
import api from './api'

export const accountsApi = {
  getCashAndBank: (): Promise<any> => api.get('/get_accounts'),
  getAll: (params: Record<string, any> = {}): Promise<any> => api.get('/accounts', { params }),
  getById: (id: number | string): Promise<any> => api.get(`/accounts/${id}`),
  create: (data: Record<string, any>): Promise<any> => api.post('/accounts', data),
  update: (id: number | string, data: Record<string, any>): Promise<any> => api.put(`/accounts/${id}`, data),
  delete: (id: number | string): Promise<any> => api.delete(`/accounts/${id}`),
  getCreateData: (): Promise<any> => api.get('/accounts/create'),
  // F-03 — seeded account types chart (id, name, category, normal_balance)
  // powering the typed account create/edit form.
  getAccountTypes: (): Promise<any> => api.get('/account_types', { params: { typed: true } }),
  importAccounts: (formData: FormData): Promise<any> => api.post('/accounts/import', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  exportAccounts: (): Promise<any> => api.get('/accounts/export', { responseType: 'blob' }),
  toggleActive: (id: number | string): Promise<any> => api.put(`/accounts/${id}/toggle-active`),
  reparent: (id: number | string, parentId: number | string | null): Promise<any> => api.put(`/accounts/${id}/reparent`, { parent_id: parentId }),
  getDiagnostics: (): Promise<any> => api.get('/accounts/diagnostics'),
}

export const depositsApi = {
  getAll: (params: Record<string, any> = {}): Promise<any> => api.get('/deposits', { params }),
  getById: (id: number | string): Promise<any> => api.get(`/deposits/${id}`),
  create: (data: Record<string, any>): Promise<any> => api.post('/deposits', data),
  update: (id: number | string, data: Record<string, any>): Promise<any> => api.put(`/deposits/${id}`, data),
  delete: (id: number | string): Promise<any> => api.delete(`/deposits/${id}`),
  getCategories: (params: Record<string, any> = {}): Promise<any> => api.get('/deposits/categories', { params }),
  getCreateData: (): Promise<any> => api.get('/deposits/create'),
  deleteCategory: (id: number | string): Promise<any> => api.delete(`/deposits/categories/${id}`),
}

export const expensesApi = {
  getAll: (params: Record<string, any> = {}): Promise<any> => api.get('/expenses', { params }),
  getById: (id: number | string): Promise<any> => api.get(`/expenses/${id}`),
  create: (data: Record<string, any>): Promise<any> => api.post('/expenses', data),
  update: (id: number | string, data: Record<string, any>): Promise<any> => api.put(`/expenses/${id}`, data),
  delete: (id: number | string): Promise<any> => api.delete(`/expenses/${id}`),
  getDetails: (id: number | string): Promise<any> => api.get(`/expenses/${id}/details`),
  getCategories: (params: Record<string, any> = {}): Promise<any> => api.get('/expense_categories', { params }),
  createCategory: (data: Record<string, any>): Promise<any> => api.post('/expense_categories', data),
  updateCategory: (id: number | string, data: Record<string, any>): Promise<any> => api.put(`/expense_categories/${id}`, data),
  deleteCategory: (id: number | string): Promise<any> => api.delete(`/expense_categories/${id}`),
  deleteCategoriesBySelection: (selectedIds: (number | string)[]): Promise<any> => api.post('/expense_categories_delete_by_selection', { selectedIds }),
  getDetailCategories: (params: Record<string, any> = {}): Promise<any> => api.get('/expense_detail_categories', { params }),
  getCreateData: (): Promise<any> => api.get('/expenses/create'),
  getPayments: (expenseId: number | string): Promise<any> =>
    api.get('/payments', {
      params: {
        paymentable_type: 'Modules\\Accounting\\Models\\Expense',
        paymentable_id: expenseId,
      },
    }),
  deletePayment: (paymentId: number | string): Promise<any> => api.delete(`/payments/${paymentId}`),
  updatePayment: (paymentId: number | string, data: Record<string, any>): Promise<any> => api.put(`/payments/${paymentId}`, data),
  getSuppliers: (): Promise<any> => api.get('/get_all_suppliers'),
  getAccounts: (): Promise<any> => api.get('/get_accounts'),
  getCategoryById: (id: number | string): Promise<any> => api.get(`/expenses_category/${id}`),
  getCategoryExpenses: (categoryId: number | string, params: Record<string, any> = {}): Promise<any> => api.get(`/expenses_category/${categoryId}/expenses`, { params }),
}

export const cashWithdrawalsApi = {
  getAll: (params: Record<string, any> = {}): Promise<any> => api.get('/personal_withdrawals/cash', { params }),
  getById: (id: number | string): Promise<any> => api.get(`/personal_withdrawals/cash/${id}`),
  create: (data: Record<string, any>): Promise<any> => api.post('/personal_withdrawals/store_cash', data),
  update: (id: number | string, data: Record<string, any>): Promise<any> => api.put(`/personal_withdrawals/cash/${id}`, data),
  delete: (id: number | string): Promise<any> => api.delete(`/personal_withdrawals/cash/${id}`),
  getCreateData: (): Promise<any> => api.get('/personal_withdrawals/create_cash'),
  getCashWithdrawal: (id: number | string): Promise<any> => api.get(`/personal_withdrawals/cash/${id}`),
}

export const personalWithdrawalsApi = {
  getAll: (params: Record<string, any> = {}): Promise<any> => api.get('/personal_withdrawals', { params }),
  getById: (id: number | string): Promise<any> => api.get(`/personal_withdrawals/${id}`),
  create: (data: Record<string, any>): Promise<any> => api.post('/personal_withdrawals', data),
  update: (id: number | string, data: Record<string, any>): Promise<any> => api.put(`/personal_withdrawals/${id}`, data),
  delete: (id: number | string): Promise<any> => api.delete(`/personal_withdrawals/${id}`),
  getCreateData: (): Promise<any> => api.get('/personal_withdrawals/create'),
}

export const fixedAssetsApi = {
  getAll: (params: Record<string, any> = {}): Promise<any> => api.get('/fixed-assets', { params }),
  getById: (id: number | string): Promise<any> => api.get(`/fixed-assets/${id}`),
  create: (data: Record<string, any>): Promise<any> => api.post('/fixed-assets', data),
  update: (id: number | string, data: Record<string, any>): Promise<any> => {
    if (data instanceof FormData) {
      data.set('_method', 'PUT');
      return api.post(`/fixed-assets/${id}`, data);
    }
    return api.put(`/fixed-assets/${id}`, data);
  },
  delete: (id: number | string): Promise<any> => api.delete(`/fixed-assets/${id}`),
  deleteBySelection: (ids: (number | string)[]): Promise<any> => api.post('/fixed-assets/delete/by_selection', { ids }),
  getCreateData: (): Promise<any> => api.get('/fixed-assets/create'),
  generateCode: (): Promise<any> => api.get('/fixed-assets/generate_code'),
  dispose: (id: number | string, data: Record<string, any>): Promise<any> => api.post(`/fixed-assets/${id}/dispose`, data),
  getPayments: (id: number | string): Promise<any> => api.get(`/fixed-assets/${id}/payments`),
  deletePayment: (paymentId: number | string): Promise<any> => api.delete(`/fixed-asset-payments/${paymentId}`),
  getDepreciationSchedule: (id: number | string): Promise<any> => api.get(`/fixed-assets/${id}/depreciation-schedule`),
  createLocation: (data: Record<string, any>): Promise<any> => api.post('/fixed-assets/locations', data),
  updateLocation: (id: number | string, data: Record<string, any>): Promise<any> => api.put(`/fixed-assets/locations/${id}`, data),
  deleteLocation: (id: number | string): Promise<any> => api.delete(`/fixed-assets/locations/${id}`),
  getAllLocations: (params: Record<string, any> = {}): Promise<any> => api.get('/fixed-assets/locations', { params }),
  getCategories: (params: Record<string, any> = {}): Promise<any> => api.get('/fixed-assets/categories', { params }),
  createCategory: (data: Record<string, any>): Promise<any> => api.post('/fixed-assets/categories', data),
  updateCategory: (id: number | string, data: Record<string, any>): Promise<any> => api.put(`/fixed-assets/categories/${id}`, data),
  deleteCategory: (id: number | string): Promise<any> => api.delete(`/fixed-assets/categories/${id}`),
  getDepreciationReport: (params: Record<string, any> = {}): Promise<any> => api.get('/fixed-assets/report/depreciation', { params }),
  revaluate: (id: number | string, data: Record<string, any>): Promise<any> => api.post(`/fixed-assets/${id}/revaluate`, data),
  getRevaluationHistory: (id: number | string): Promise<any> => api.get(`/fixed-assets/${id}/revaluation-history`),
  getDepreciationSchedules: (params?: Record<string, any>): Promise<any> =>
    api.get('/depreciation-schedules', { params }),
  getDepreciationScheduleById: (id: number | string): Promise<any> =>
    api.get(`/depreciation-schedules/${id}`),
  createDepreciationSchedule: (data: Record<string, any>): Promise<any> =>
    api.post('/depreciation-schedules', data),
  updateDepreciationSchedule: (id: number | string, data: Record<string, any>): Promise<any> =>
    api.put(`/depreciation-schedules/${id}`, data),
  deleteDepreciationSchedule: (id: number | string): Promise<any> =>
    api.delete(`/depreciation-schedules/${id}`),
  activateDepreciationSchedule: (id: number | string): Promise<any> =>
    api.post(`/depreciation-schedules/${id}/activate`),
  deactivateDepreciationSchedule: (id: number | string): Promise<any> =>
    api.post(`/depreciation-schedules/${id}/deactivate`),
  runDepreciationSchedule: (id: number | string): Promise<any> =>
    api.post(`/depreciation-schedules/${id}/run`),
  getDepreciationScheduleHistory: (id: number | string, params?: Record<string, any>): Promise<any> =>
    api.get(`/depreciation-schedules/${id}/history`, { params }),
  previewDepreciationScheduleRun: (id: number | string): Promise<any> =>
    api.get(`/depreciation-schedules/${id}/preview`),
}

export const transfersApi = {
  getAll: (params: Record<string, any> = {}): Promise<any> => api.get('/transfers', { params }),
  getById: (id: number | string): Promise<any> => api.get(`/transfers/${id}`),
  create: (data: Record<string, any>): Promise<any> => api.post('/transfers', data),
  update: (id: number | string, data: Record<string, any>): Promise<any> => api.put(`/transfers/${id}`, data),
  delete: (id: number | string): Promise<any> => api.delete(`/transfers/${id}`),
  deleteBySelection: (selectedIds: (number | string)[]): Promise<any> => api.post('/transfers/delete/by_selection', { selectedIds }),
}

export const transferMoneyApi = {
  getAll: (params: Record<string, any> = {}): Promise<any> => api.get('/transfer_money', { params }),
  create: (data: Record<string, any>): Promise<any> => api.post('/transfer_money', data),
  update: (id: number | string, data: Record<string, any>): Promise<any> => api.put(`/transfer_money/${id}`, data),
  delete: (id: number | string): Promise<any> => api.delete(`/transfer_money/${id}`),
}

export const custodiesApi = {
  // Backend routes are literal: GET custody/details/{id}, POST custody/create,
  // PUT custody/update/{id} — there is no /custodies resource.
  getDetails: (id: number | string): Promise<any> => api.get(`/custody/details/${id}`),
  create: (data: Record<string, any>): Promise<any> => api.post('/custody/create', data),
  update: (id: number | string, data: Record<string, any>): Promise<any> => api.put(`/custody/update/${id}`, data),
}

export const financialStatementsApi = {
  context: (companyId: string): Promise<any> => api.get('/financial-reports/context', { params: { expected_company_id: companyId } }),
  preview: (data: { expected_company_id: string; report_type?: string; from?: string; to: string; mode: 'actual' | 'simulation'; warehouse_id?: number; book_id?: number; cost_center_id?: number; dimension_id?: number; dimension_value_id?: number; sources?: string[]; journal_ids?: number[]; payroll_period_ids?: number[]; trial_balance_type?: string; include_zero_accounts?: boolean; include_account_hierarchy?: boolean; cash_flow_method?: 'indirect' | 'direct'; compare_from?: string; compare_to?: string }): Promise<any> => api.post('/financial-reports/preview', data),
  exportPreview: (token: string, format: 'pdf' | 'xlsx', locale: string): Promise<any> => api.post(`/financial-reports/export/${format}`, { capture_token: token, locale: normalizeLocale(locale) ?? defaultLocale }, { responseType: 'blob' }),
  getAll: (params: Record<string, any> = {}): Promise<any> => api.get('/financial-statements', { params }),
  getIncomeStatement: (params: Record<string, any> = {}): Promise<any> => api.get('/report/income_statement', { params }),
  getBalanceSheet: (params: Record<string, any> = {}): Promise<any> => api.get('/report/balance_sheet', { params }),
  getCashFlowStatement: (params: Record<string, any> = {}): Promise<any> => api.get('/statements/cash-flow-indirect', { params }),
  getComparativeBalanceSheet: (params: Record<string, any> = {}): Promise<any> => api.get('/report/comparative/balance-sheet', { params }),
  getComparativeIncomeStatement: (params: Record<string, any> = {}): Promise<any> => api.get('/report/comparative/income-statement', { params }),
  getEquityStatement: (params: Record<string, any> = {}): Promise<any> => api.get('/report/equity-statement', { params }),
  getRetainedEarningsStatement: (params: Record<string, any> = {}): Promise<any> => api.get('/report/equity-statement', { params }),
  getRetainedEarningsConfig: (): Promise<any> => api.get('/report/retained-earnings-config'),
  getClosingChecklist: (fiscalPeriodId: number | string): Promise<any> => api.get(`/fiscal-periods/${fiscalPeriodId}/closing-checklist`),
  updateRetainedEarningsConfig: (data: Record<string, any>): Promise<any> => api.put('/report/retained-earnings-config', data),
  getTrialBalance: (params: Record<string, any> = {}): Promise<any> => api.get('/report/trial_balance', { params }),
  getTrialBalanceComparison: (params: Record<string, any> = {}): Promise<any> => api.get('/report/trial_balance/comparison', { params }),
  getTrialBalanceThreeColumn: (params: Record<string, any> = {}): Promise<any> => api.get('/report/trial_balance/comparison', { params }),
  getTrialBalanceGrouped: (params: Record<string, any> = {}): Promise<any> => api.get('/report/trial_balance/grouped', { params }),
  getTrialBalanceWithSubAccounts: (params: Record<string, any> = {}): Promise<any> => api.get('/report/trial_balance/with-sub-accounts', { params }),
  getCashFlowDirect: (params: Record<string, any> = {}): Promise<any> => api.get('/statements/cash-flow-direct', { params }),
  getCashFlowIndirect: (params: { start_date: string; end_date: string; warehouse_id?: number }): Promise<any> =>
    api.get('/statements/cash-flow-indirect', { params }),
  getComparativeStatement: (type: string, params: Record<string, any> = {}): Promise<any> => api.get(`/statements/comparative/${type}`, { params }),
  getIntegrityCheck: (params: Record<string, any> = {}): Promise<any> => api.get('/report/integrity-check', { params }),
  getControlReadiness: (params: Record<string, any> = {}): Promise<any> => api.get('/report/control-readiness', { params }),
  getChangeExplanation: (params: Record<string, any> = {}): Promise<any> => api.get('/report/change-explanation', { params }),
  getFixedAssetRegister: (params: Record<string, any> = {}): Promise<any> => api.get('/report/fixed-asset-register', { params }),
  getDocumentPostingTimeline: (params: Record<string, any> = {}): Promise<any> => api.get('/report/document-posting-timeline', { params }),
  getPartyStatement: (params: Record<string, any> = {}): Promise<any> => api.get('/report/party-statement', { params }),
  getDimensions: (expectedCompanyId?: string): Promise<any> => api.get('/dimensional-reports/dimensions', { params: { expected_company_id: expectedCompanyId } }),
  getDimensionalCrossTab: (data: { expected_company_id?: string; dimension_id: number; from_date?: string; to_date?: string }): Promise<any> => api.post('/dimensional-reports/cross-tab', data),
  getFinancialKpis: (params: Record<string, any> = {}): Promise<any> => api.get('/report/financial-kpis', { params }),
  getFinancialKpiTrend: (params: Record<string, any> = {}): Promise<any> => api.get('/report/financial-kpis/trend', { params }),
}

export const glFinancialStatementsApi = {
  getIncomeStatement: (params: Record<string, any> = {}): Promise<any> => api.get('/report/gl/income-statement', { params }),
  getBalanceSheet: (params: Record<string, any> = {}): Promise<any> => api.get('/report/gl/balance-sheet', { params }),
  getIncomeStatementPdf: (params: Record<string, any> = {}): Promise<any> => api.get('/report/gl/income-statement/export/pdf', { params, responseType: 'blob' }),
  getBalanceSheetPdf: (params: Record<string, any> = {}): Promise<any> => api.get('/report/gl/balance-sheet/export/pdf', { params, responseType: 'blob' }),
}

export const bankTransferFormatsApi = {
  getAll: (params?: Record<string, any>): Promise<any> =>
    api.get('/bank-transfer-formats', { params }),
  getById: (id: number | string): Promise<any> =>
    api.get(`/bank-transfer-formats/${id}`),
  create: (data: Record<string, any>): Promise<any> =>
    api.post('/bank-transfer-formats', data),
  update: (id: number | string, data: Record<string, any>): Promise<any> =>
    api.put(`/bank-transfer-formats/${id}`, data),
  delete: (id: number | string): Promise<any> =>
    api.delete(`/bank-transfer-formats/${id}`),
  // Bank transfer FILES are generated per payroll period:
  // GET payroll-periods/{id}/bank-transfer-file
  validate: (id: number | string): Promise<any> =>
    api.post(`/bank-transfer-formats/${id}/validate`),
  seed: (): Promise<any> =>
    api.post('/bank-transfer-formats/seed'),
  setDefault: (id: number | string): Promise<any> =>
    api.post(`/bank-transfer-formats/${id}/set-default`),
}

export const paymentsApi = {
  getAll: (params: Record<string, any> = {}): Promise<any> => api.get('/payments', { params }),
  getById: (id: number | string): Promise<any> => api.get(`/payments/${id}`),
  create: (data: Record<string, any>): Promise<any> => api.post('/payments', data),
  update: (id: number | string, data: Record<string, any>): Promise<any> => api.put(`/payments/${id}`, data),
  delete: (id: number | string): Promise<any> => api.delete(`/payments/${id}`),
}

export const reconciliationApi = {
  getControlAccountReconciliation: (params: Record<string, any> = {}): Promise<any> =>
    api.get('/accounting/reconciliation/control-accounts', { params }),
  runControlAccountReconciliation: (payload: Record<string, any> = {}): Promise<any> =>
    api.post('/accounting/reconciliation/control-accounts/run', payload),
  getOperationalVsGL: (params: Record<string, any> = {}): Promise<any> =>
    api.get('/accounting/reconciliation/operational-vs-gl', { params }),
}

export const recurringJournalsApi = {
  getAll: (params: Record<string, any> = {}): Promise<any> =>
    api.get('/recurring-journal-templates', { params }),
  getById: (id: number | string): Promise<any> =>
    api.get(`/recurring-journal-templates/${id}`),
  create: (data: Record<string, any>): Promise<any> =>
    api.post('/recurring-journal-templates', data),
  update: (id: number | string, data: Record<string, any>): Promise<any> =>
    api.put(`/recurring-journal-templates/${id}`, data),
  delete: (id: number | string): Promise<any> =>
    api.delete(`/recurring-journal-templates/${id}`),
  process: (): Promise<any> =>
    api.post('/recurring-journal-templates/process'),
  preview: (id: number | string): Promise<any> =>
    api.get(`/recurring-journal-templates/${id}/preview`),
  schedule: (id: number | string, params: Record<string, any> = {}): Promise<any> =>
    api.get(`/recurring-journal-templates/${id}/schedule`, { params }),
}

export const journalTemplatesApi = {
  getAll: (params: Record<string, any> = {}): Promise<any> =>
    api.get('/journal-templates', { params }),
  getById: (id: number | string): Promise<any> =>
    api.get(`/journal-templates/${id}`),
  create: (data: Record<string, any>): Promise<any> =>
    api.post('/journal-templates', data),
  update: (id: number | string, data: Record<string, any>): Promise<any> =>
    api.put(`/journal-templates/${id}`, data),
  delete: (id: number | string): Promise<any> =>
    api.delete(`/journal-templates/${id}`),
  apply: (id: number | string, overrides: Record<string, any> = {}): Promise<any> =>
    api.get(`/journal-templates/${id}/apply`, { params: { overrides } }),
  generateFromJournal: (journalId: number | string): Promise<any> =>
    api.post('/journal-templates/generate-from-journal', { journal_id: journalId }),
}

export const budgetApi = {
  getAll: (params: Record<string, any> = {}): Promise<any> =>
    api.get('/budgets', { params: { ...params, perPage: params.limit ?? params.perPage, name: params.search ?? params.name } }),
  getById: (id: number | string): Promise<any> =>
    api.get(`/budgets/${id}`),
  create: (data: Record<string, any>): Promise<any> =>
    api.post('/budgets', data),
  update: (id: number | string, data: Record<string, any>): Promise<any> =>
    api.put(`/budgets/${id}`, data),
  delete: (id: number | string): Promise<any> =>
    api.delete(`/budgets/${id}`),
  approve: (id: number | string): Promise<any> =>
    api.post(`/budgets/${id}/approve`),
  getVariance: (id: number | string, params: Record<string, any> = {}): Promise<any> =>
    api.get(`/budgets/${id}/variance`, { params }),
  computeVariance: (id: number | string): Promise<any> =>
    api.post(`/budgets/${id}/compute-variance`),
  createEncumbrance: (id: number | string, data: Record<string, any>): Promise<any> =>
    api.post(`/budgets/${id}/encumbrances`, data),
  liquidateEncumbrance: (budgetId: number | string, encumbranceId: number | string): Promise<any> =>
    api.post(`/budgets/${budgetId}/encumbrances/${encumbranceId}/liquidate`),
}

export const accountingApi = {
  // Namespaced sub-APIs. Previously every sub-API was ALSO blanket-spread into
  // this object, so same-named CRUD methods (getAll/getById/create/update/
  // delete) silently resolved to whichever API was spread last
  // (journalTemplatesApi) — any such call would hit the wrong endpoint. Only
  // the handful of methods consumers actually call directly are aliased below.
  fixedAssetsApi,
  accountsApi,
  depositsApi,
  expensesApi,
  cashWithdrawalsApi,
  personalWithdrawalsApi,
  transfersApi,
  transferMoneyApi,
  custodiesApi,
  financialStatementsApi,
  paymentsApi,
  reconciliationApi,
  budgetApi,
  recurringJournalsApi,
  journalTemplatesApi,
  getAccounts: accountsApi.getAll,
  getCustodyDetails: custodiesApi.getDetails,
  getDepreciationReport: fixedAssetsApi.getDepreciationReport,
  getAllLocations: fixedAssetsApi.getAllLocations,
  createLocation: fixedAssetsApi.createLocation,
  updateLocation: fixedAssetsApi.updateLocation,
  deleteLocation: fixedAssetsApi.deleteLocation,
}

export const getDepreciationReport = (params: Record<string, any> = {}): Promise<any> => api.get('/fixed-assets/report/depreciation', { params })

export const getAllLocations = (params: Record<string, any> = {}): Promise<any> => api.get('/fixed-assets/locations', { params })
export const createLocation = (data: Record<string, any>): Promise<any> => api.post('/fixed-assets/locations', data)
export const updateLocation = (id: number | string, data: Record<string, any>): Promise<any> => api.put(`/fixed-assets/locations/${id}`, data)
export const deleteLocation = (id: number | string): Promise<any> => api.delete(`/fixed-assets/locations/${id}`)

import { apiSlice } from '@/redux/api/apiSlice';

export const accountingRtkApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getRecurringJournals: builder.query<any, Record<string, any> | void>({
      query: (params) => ({
        url: '/recurring-journal-templates',
        params,
      }),
      providesTags: ['Accounting'],
    }),
    createRecurringJournal: builder.mutation<any, Record<string, any>>({
      query: (data) => ({
        url: '/recurring-journal-templates',
        method: 'POST',
        data,
      }),
      invalidatesTags: ['Accounting'],
    }),
    updateRecurringJournal: builder.mutation<any, { id: number | string; data: Record<string, any> }>({
      query: ({ id, data }) => ({
        url: `/recurring-journal-templates/${id}`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: ['Accounting'],
    }),
    deleteRecurringJournal: builder.mutation<any, number | string>({
      query: (id) => ({
        url: `/recurring-journal-templates/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Accounting'],
    }),
    processRecurringJournals: builder.mutation<any, void>({
      query: () => ({
        url: '/recurring-journal-templates/process',
        method: 'POST',
      }),
      invalidatesTags: ['Accounting'],
    }),
    previewRecurringJournal: builder.mutation<any, number | string>({
      query: (id) => ({
        url: `/recurring-journal-templates/${id}/preview`,
        method: 'GET',
      }),
    }),
    scheduleRecurringJournal: builder.mutation<any, { id: number | string; params?: Record<string, any> }>({
      query: ({ id, params }) => ({
        url: `/recurring-journal-templates/${id}/schedule`,
        method: 'GET',
        params,
      }),
    }),
    getAccounts: builder.query<any, Record<string, any> | void>({
      query: (params) => ({
        url: '/accounts',
        params,
      }),
      providesTags: ['Accounting'],
    }),
  }),
});

export const {
  useGetRecurringJournalsQuery,
  useCreateRecurringJournalMutation,
  useUpdateRecurringJournalMutation,
  useDeleteRecurringJournalMutation,
  useProcessRecurringJournalsMutation,
  usePreviewRecurringJournalMutation,
  useScheduleRecurringJournalMutation,
  useGetAccountsQuery,
} = accountingRtkApi;

export default accountingApi

export const financialStatementsRtkApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getEquityStatement: builder.query<any, Record<string, any> | void>({
      query: (params) => ({
        url: '/report/equity-statement',
        params: params || undefined,
      }),
    }),
    getComparison: builder.query<any, Record<string, any> | void>({
      query: (params) => ({
        url: '/report/comparative/income-statement',
        params: params || undefined,
      }),
    }),
    getIncomeStatementGl: builder.query<any, Record<string, any> | void>({
      query: (params) => ({
        url: '/report/gl/income-statement',
        params: params || undefined,
      }),
    }),
    getIncomeStatementGlPdf: builder.query<any, Record<string, any> | void>({
      query: (params) => ({
        url: '/report/gl/income-statement/export/pdf',
        params: params || undefined,
        responseType: 'blob',
      }),
    }),
  }),
});

export const {
  useLazyGetEquityStatementQuery,
  useLazyGetComparisonQuery,
  useLazyGetIncomeStatementGlQuery,
  useLazyGetIncomeStatementGlPdfQuery,
} = financialStatementsRtkApi;

// Real RTK endpoints backed by routes in app/Modules/Accounting/Routes/api.php.
// draft-payments: GET /draft-payments, POST /payments/finalize-batch, DELETE /payments/{id}
// draft-journals: GET /draft-journals, POST /draft-journals/{id}/finalize, /draft-journals/finalize-all
// integrity: GET /report/integrity-check, GET /report/integrity-status, POST /accounting/audit-chain/verify
export const accountingDraftsAndIntegrityApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getDraftPayments: builder.query<any, Record<string, any> | void>({
      query: (params) => ({
        url: '/draft-payments',
        params: params || {},
      }),
      providesTags: ['Accounting'],
    }),
    finalizeDraftPaymentsBatch: builder.mutation<any, (number | string)[]>({
      query: (ids) => ({
        url: '/payments/finalize-batch',
        method: 'POST',
        data: { ids },
      }),
      invalidatesTags: ['Accounting'],
    }),
    deleteDraftPayment: builder.mutation<any, number | string>({
      query: (id) => ({
        url: `/payments/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Accounting'],
    }),
    getDraftJournals: builder.query<any, Record<string, any> | void>({
      query: (params) => ({
        url: '/draft-journals',
        params: params || {},
      }),
      providesTags: ['Accounting'],
    }),
    getDraftJournal: builder.query<any, number | string>({
      query: (id) => ({ url: `/draft-journals/${id}` }),
      providesTags: (result, error, id) => [{ type: 'Accounting', id }],
    }),
    finalizeDraftJournal: builder.mutation<any, number | string>({
      query: (id) => ({
        url: `/draft-journals/${id}/finalize`,
        method: 'POST',
      }),
      invalidatesTags: ['Accounting'],
    }),
    finalizeAllDraftJournals: builder.mutation<any, void>({
      query: () => ({
        url: '/draft-journals/finalize-all',
        method: 'POST',
      }),
      invalidatesTags: ['Accounting'],
    }),
    batchPostManualJournals: builder.mutation<any, (number | string)[]>({
      query: (ids) => ({
        url: '/manual-journals/batch-post',
        method: 'POST',
        data: { ids },
      }),
      invalidatesTags: ['ManualJournal'],
    }),
    getIntegrityCheck: builder.query<any, Record<string, any> | void>({
      query: (params) => ({
        url: '/report/integrity-check',
        params: params || {},
      }),
    }),
    verifyGlIntegrity: builder.mutation<any, Record<string, any> | void>({
      query: (params) => ({
        url: '/report/integrity-status',
        params: params || {},
        method: 'GET',
      }),
    }),
    verifyAuditChain: builder.query<any, Record<string, any> | void>({
      query: (params) => ({
        url: '/accounting/audit-chain/verify',
        method: 'POST',
        data: params || {},
      }),
    }),
  }),
});

export const {
  useGetDraftPaymentsQuery,
  useFinalizeDraftPaymentsBatchMutation,
  useDeleteDraftPaymentMutation,
  useGetDraftJournalsQuery,
  useGetDraftJournalQuery,
  useFinalizeDraftJournalMutation,
  useFinalizeAllDraftJournalsMutation,
  useBatchPostManualJournalsMutation,
  useGetIntegrityCheckQuery,
  useVerifyGlIntegrityMutation,
  useVerifyAuditChainQuery,
} = accountingDraftsAndIntegrityApi;

export { useCreateManualJournalMutation } from '@/redux/api/accountingLedgerApi';


export {
  useGetCostCentersQuery,
  useGetCostCenterTreeQuery,
  useCreateCostCenterMutation,
  useUpdateCostCenterMutation,
  useDeleteCostCenterMutation,
  useLazyGetTrialBalanceQuery,
  useLazyGetIncomeStatementQuery,
  useRunCurrencyRevaluationMutation as useCurrencyRevaluationMutation,
} from '@/redux/api/costCenterApi';

export {
  useGetPersonalWithdrawalsQuery,
  useGetPersonalWithdrawalQuery,
  useDeletePersonalWithdrawalMutation,
  useDeleteSelectedPersonalWithdrawalsMutation,
  useGetCashWithdrawalsQuery,
  useGetCashWithdrawalQuery,
  useDeleteCashWithdrawalMutation,
  useDeleteSelectedCashWithdrawalsMutation,
  useUpdateCashWithdrawalStatusMutation,
} from '@/redux/api/personalWithdrawalsApi';

export {
  useGetFiscalPeriodsQuery,
  useGetAllFiscalPeriodsQuery,
  useGetCurrentFiscalPeriodQuery,
  useGetValidationChecklistQuery,
  useLazyGetValidationChecklistQuery,
  useRunPeriodValidationsMutation,
  useClosePeriodMutation,
  usePreviewCarryForwardMutation,
  useCarryForwardMutation,
  useVerifyCarryForwardMutation,
} from '@/redux/api/fiscalPeriodsApi';



export {
  useGetBudgetsQuery,
  useGetBudgetByIdQuery,
  useCreateBudgetMutation,
  useUpdateBudgetMutation,
  useDeleteBudgetMutation,
  useApproveBudgetMutation,
  useGetVarianceQuery as useGetBudgetVarianceQuery,
  useComputeVarianceMutation as useComputeBudgetVarianceMutation,
  useCreateEncumbranceMutation,
  useLiquidateEncumbranceMutation,
} from '@/redux/api/budgetApi'

export {
  useGetAssetsQuery,
  useGetAllAssetsQuery,
  useGetAssetByIdQuery,
  useCreateAssetMutation,
  useUpdateAssetMutation,
  useDeleteAssetMutation,
  useDeleteMultipleAssetsMutation,
  useRevaluateAssetMutation,
  useGetCategoriesQuery,
  useCreateCategoryMutation,
  useUpdateCategoryMutation,
  useDeleteCategoryMutation,
  useGetLocationsQuery,
  useCreateLocationMutation,
  useUpdateLocationMutation,
  useDeleteLocationMutation,
  useGetDepreciationReportQuery,
  useGetDepreciationSchedulesQuery,
  useCreateDepreciationScheduleMutation,
  useUpdateDepreciationScheduleMutation,
  useDeleteDepreciationScheduleMutation,
  useActivateDepreciationScheduleMutation,
  useDeactivateDepreciationScheduleMutation,
  useRunDepreciationScheduleMutation,
  usePreviewDepreciationScheduleRunMutation,
  useGetDepreciationScheduleHistoryQuery,
} from '@/redux/api/fixedAssetsApi';


